import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { apiFetch } from '../../shared/lib/apiClient';
import logo from '../../shared/assets/logos/ARBCOR_Text_only.png';
import logoMark from '../../shared/assets/logos/BlackLogoPNG_TransparentBackground.png';
import NetworkCanvas from '../../shared/components/NetworkCanvas';

const ease = 'cubic-bezier(0.16,1,0.3,1)';

const Signup = () => {
  const [loaded, setLoaded] = useState(false);
  const { signUp, signIn } = useAuth();
  const navigate = useNavigate();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  const clearError = () => { if (error) setError(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!fullName || !email || !password) {
      setError('Please fill in all fields.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setError('');
    setSubmitting(true);

    try {
      await signUp(email, password, fullName);
      await signIn(email, password, fullName);

      apiFetch('/api/auth/welcome-email', {
        method: 'POST',
        body: JSON.stringify({ fullName, email }),
      }).catch(() => {});

      navigate('/onboarding');
    } catch (err) {
      console.error('Signup error:', err);
      if (err.code === 'invalid_password') {
        const policy = err.policy || err.description?.policy;
        setError(typeof policy === 'string' ? policy : 'Password does not meet requirements.');
      } else if (err.code === 'user_exists') {
        setError('This email is already registered. Try signing in instead.');
      } else {
        const desc = err.description;
        setError((typeof desc === 'string' ? desc : null) || err.message || 'Signup failed. Please try again.');
      }
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex relative overflow-hidden">
      {/* Mobile background */}
      <div
        className={`absolute right-[-15%] top-1/2 -translate-y-1/2 lg:hidden transition-all duration-[1500ms] ${
          loaded ? 'opacity-[0.025] scale-100' : 'opacity-0 scale-[0.95]'
        }`}
        style={{ transitionTimingFunction: ease, transitionDelay: '300ms' }}
      >
        <img src={logoMark} alt="" className="w-[20rem] sm:w-[28rem] h-auto select-none pointer-events-none" draggable="false" />
      </div>

      {/* Left: form */}
      <div className="w-full lg:w-5/12 min-h-screen flex flex-col px-6 sm:px-12 lg:px-16 xl:px-24 relative z-10">
        {/* Logo */}
        <div
          className="pt-8 pb-4"
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? 'translateY(0)' : 'translateY(-8px)',
            transition: `all 0.8s ${ease}`,
          }}
        >
          <Link to="/">
            <img src={logo} alt="Arbcor" className="h-5 w-auto select-none" draggable="false" />
          </Link>
        </div>

        {/* Form */}
        <div className="flex-1 flex flex-col justify-center pb-12">
          <div className="w-full max-w-sm">
            <h1
              className="text-[24px] font-semibold text-zinc-900 mb-1"
              style={{
                opacity: loaded ? 1 : 0,
                transform: loaded ? 'translateY(0)' : 'translateY(8px)',
                transition: `all 0.6s ${ease}`,
                transitionDelay: '200ms',
              }}
            >
              Create your account
            </h1>
            <p
              className="text-[14px] text-zinc-400 mb-8"
              style={{
                opacity: loaded ? 1 : 0,
                transform: loaded ? 'translateY(0)' : 'translateY(8px)',
                transition: `all 0.6s ${ease}`,
                transitionDelay: '280ms',
              }}
            >
              Get started with Arbcor in seconds
            </p>

            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div
                  style={{
                    opacity: loaded ? 1 : 0,
                    transform: loaded ? 'translateY(0)' : 'translateY(8px)',
                    transition: `all 0.5s ${ease}`,
                    transitionDelay: '350ms',
                  }}
                >
                  <label className="block text-[11px] uppercase tracking-[0.1em] text-zinc-400 font-medium mb-1.5">Full Name</label>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => { setFullName(e.target.value); clearError(); }}
                    placeholder="Jane Smith"
                    autoComplete="name"
                    disabled={submitting}
                    className={`w-full px-3.5 py-2.5 border text-[14px] text-zinc-900 placeholder-zinc-300 rounded-md outline-none transition-colors disabled:opacity-50 ${
                      error ? 'border-red-300' : 'border-zinc-200 focus:border-zinc-900'
                    }`}
                  />
                </div>

                <div
                  style={{
                    opacity: loaded ? 1 : 0,
                    transform: loaded ? 'translateY(0)' : 'translateY(8px)',
                    transition: `all 0.5s ${ease}`,
                    transitionDelay: '420ms',
                  }}
                >
                  <label className="block text-[11px] uppercase tracking-[0.1em] text-zinc-400 font-medium mb-1.5">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); clearError(); }}
                    placeholder="you@company.com"
                    autoComplete="email"
                    disabled={submitting}
                    className={`w-full px-3.5 py-2.5 border text-[14px] text-zinc-900 placeholder-zinc-300 rounded-md outline-none transition-colors disabled:opacity-50 ${
                      error ? 'border-red-300' : 'border-zinc-200 focus:border-zinc-900'
                    }`}
                  />
                </div>

                <div
                  style={{
                    opacity: loaded ? 1 : 0,
                    transform: loaded ? 'translateY(0)' : 'translateY(8px)',
                    transition: `all 0.5s ${ease}`,
                    transitionDelay: '490ms',
                  }}
                >
                  <label className="block text-[11px] uppercase tracking-[0.1em] text-zinc-400 font-medium mb-1.5">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); clearError(); }}
                      placeholder="Min. 8 characters"
                      autoComplete="new-password"
                      disabled={submitting}
                      className={`w-full px-3.5 py-2.5 pr-10 border text-[14px] text-zinc-900 placeholder-zinc-300 rounded-md outline-none transition-colors disabled:opacity-50 ${
                        error ? 'border-red-300' : 'border-zinc-200 focus:border-zinc-900'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition-colors"
                      tabIndex={-1}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        {showPassword ? (
                          <>
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                            <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                            <line x1="1" y1="1" x2="23" y2="23" />
                          </>
                        ) : (
                          <>
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </>
                        )}
                      </svg>
                    </button>
                  </div>
                </div>

                {error && <p className="text-[12px] text-red-500">{error}</p>}

                <div
                  style={{
                    opacity: loaded ? 1 : 0,
                    transform: loaded ? 'translateY(0)' : 'translateY(8px)',
                    transition: `all 0.5s ${ease}`,
                    transitionDelay: '560ms',
                  }}
                >
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full mt-2 px-4 py-3 bg-zinc-900 text-white text-[14px] font-medium rounded-md hover:bg-zinc-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Creating account...
                      </>
                    ) : (
                      'Create Account'
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Bottom */}
        <div
          className="pb-8"
          style={{
            opacity: loaded ? 1 : 0,
            transition: `all 0.8s ${ease}`,
            transitionDelay: '700ms',
          }}
        >
          <p className="text-[13px] text-zinc-400">
            Already have an account?{' '}
            <Link to="/login" className="text-zinc-900 hover:text-zinc-600 transition-colors">Sign in</Link>
          </p>
        </div>
      </div>

      {/* Right: visualization */}
      <div className="hidden lg:flex w-7/12 min-h-screen bg-zinc-950 relative overflow-hidden items-center justify-center">
        <NetworkCanvas />
      </div>
    </div>
  );
};

export default Signup;
